import { ComputedRef, Ref, computed, ref } from 'vue';

import { RegistrationField } from '@/types/RegistrationField';

export enum AuthFlowMode {
  Login = 'login',
  Register = 'register',
  UpdatePassword = 'updatePassword',
  RequestPasswordReset = 'requestPasswordReset',
  ResetPassword = 'resetPassword',
  ValidateRegistration = 'validateRegistration',
  SelfRegister = 'selfRegister',
  SelfRegisterEmailSent = 'selfRegisterEmailSent',
}

export interface AuthFlowState {
  currentMode: Ref<AuthFlowMode>;
  allowPasswordReset: ComputedRef<boolean>;
  allowSelfRegistration: ComputedRef<boolean>;
  useEmailSelfRegistration: ComputedRef<boolean>;
  selfRegistrationFields: ComputedRef<RegistrationField[]>;
  buttonLabel: ComputedRef<string>;
  isLoginMode: ComputedRef<boolean>;
  isRegisterMode: ComputedRef<boolean>;
  isResetPasswordMode: ComputedRef<boolean>;
  setMode: (mode: AuthFlowMode) => void;
}

const AUTH_FLOW_LABELS: Record<AuthFlowMode, string> = {
  [AuthFlowMode.Login]: 'Login',
  [AuthFlowMode.Register]: 'Register',
  [AuthFlowMode.UpdatePassword]: 'Update Password',
  [AuthFlowMode.RequestPasswordReset]: 'Request Password Reset',
  [AuthFlowMode.ResetPassword]: 'Reset Password',
  [AuthFlowMode.ValidateRegistration]: 'Validate',
  [AuthFlowMode.SelfRegister]: 'Sign Up',
  [AuthFlowMode.SelfRegisterEmailSent]: 'Done',
};

const REGISTRATION_FIELD_TYPES: RegistrationField['type'][] = ['text', 'number', 'password'];

function isRegistrationField(value: unknown): value is RegistrationField {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const field = value as Partial<RegistrationField>;

  return (
    typeof field.name === 'string' &&
    typeof field.label === 'string' &&
    typeof field.type === 'string' &&
    REGISTRATION_FIELD_TYPES.includes(field.type as RegistrationField['type'])
  );
}

export function parseSelfRegistrationFields(raw?: string): RegistrationField[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isRegistrationField);
  } catch {
    return [];
  }
}

export function createSelfRegistrationValues(fields: RegistrationField[]): Record<string, string> {
  return fields.reduce((accumulator: Record<string, string>, field: RegistrationField) => {
    accumulator[field.name] = '';
    return accumulator;
  }, {});
}

export function useAuthFlow(): AuthFlowState {
  const currentMode = ref<AuthFlowMode>(AuthFlowMode.Login);
  const allowPasswordReset = computed(() => process.env.VUE_APP_ALLOW_PASSWORD_RESET === 'true');
  const allowSelfRegistration = computed(() => process.env.VUE_APP_ALLOW_SELF_REGISTRATION === 'true');
  const useEmailSelfRegistration = computed(() => process.env.VUE_APP_SELF_REGISTRATION_EMAIL_WORKFLOW === 'true');
  const selfRegistrationFields = computed(() =>
    parseSelfRegistrationFields(process.env.VUE_APP_SELF_REGISTRATION_FIELDS)
  );
  const buttonLabel = computed(() => AUTH_FLOW_LABELS[currentMode.value]);
  const registerModes = [
    AuthFlowMode.Register,
    AuthFlowMode.ValidateRegistration,
    AuthFlowMode.SelfRegister,
    AuthFlowMode.SelfRegisterEmailSent,
  ];
  const resetPasswordModes = [AuthFlowMode.RequestPasswordReset, AuthFlowMode.ResetPassword];

  return {
    currentMode,
    allowPasswordReset,
    allowSelfRegistration,
    useEmailSelfRegistration,
    selfRegistrationFields,
    buttonLabel,
    isLoginMode: computed(() => currentMode.value === AuthFlowMode.Login),
    isRegisterMode: computed(() => registerModes.includes(currentMode.value)),
    isResetPasswordMode: computed(() => resetPasswordModes.includes(currentMode.value)),
    setMode(mode: AuthFlowMode) {
      currentMode.value = mode;
    },
  };
}
